import { CalendarClock, Globe, Link, Lock } from 'lucide-react'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import type { HTMLAttributes, ReactNode } from 'react'

export type VideoStatus =
  | 'publicado'
  | 'agendado'
  | 'gerando'
  | 'rascunho'
  | 'falhou'
  | 'elegivel'
  | 'bloqueado'

/** A visibilidade real do vídeo no YouTube (``upload.youtube_privacy``). */
export type VideoPrivacy = 'public' | 'unlisted' | 'private'

export interface StatusChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  status: VideoStatus
  /** Override the label; defaults to a human-readable PT-BR string. */
  label?: string | undefined
  /**
   * Com ``status="publicado"``, troca estilo, ícone e rótulo pela
   * visibilidade: público (ou ainda não sincronizado) verde, não listado
   * violeta, privado âmbar. Ignorada nos outros status. O ``label``
   * explícito ainda ganha no texto, não no estilo.
   */
  privacy?: VideoPrivacy | null | undefined
}

interface ChipConfig {
  label: string
  variant: ChipVariant
  icon?: ReactNode
}

const STATUS_CONFIG: Record<VideoStatus, ChipConfig> = {
  publicado: { label: 'Publicado', variant: 'success', icon: <Globe /> },
  agendado: { label: 'Agendado', variant: 'info', icon: <CalendarClock /> },
  gerando: { label: 'Gerando', variant: 'warning' },
  rascunho: { label: 'Rascunho', variant: 'default' },
  falhou: { label: 'Falhou', variant: 'danger' },
  elegivel: { label: 'Elegível', variant: 'success' },
  bloqueado: { label: 'Bloqueado', variant: 'danger' },
}

const PRIVACY_CONFIG: Record<'unlisted' | 'private', ChipConfig> = {
  unlisted: { label: 'Não listado', variant: 'violet', icon: <Link /> },
  private: { label: 'Privado', variant: 'warning', icon: <Lock /> },
}

function chipConfig(status: VideoStatus, privacy: VideoPrivacy | null | undefined): ChipConfig {
  if (status === 'publicado' && (privacy === 'unlisted' || privacy === 'private')) return PRIVACY_CONFIG[privacy]
  return STATUS_CONFIG[status]
}

export function StatusChip({ status, label, privacy, ...rest }: StatusChipProps) {
  const config = chipConfig(status, privacy)
  return (
    <Chip variant={config.variant} leadingIcon={config.icon} {...rest}>
      {label ?? config.label}
    </Chip>
  )
}
